import React, {Ref, useContext, useEffect, useRef, useState} from 'react';
import {Alert, Button, Col, Form, Modal, Row, Select} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
// @ts-ignore
import Tags from '@yaireo/tagify/dist/react.tagify';
import {CaretDownOutlined, CloseOutlined, DeleteOutlined} from "@ant-design/icons";
import ReactQuill from "react-quill";
import {StatusResponse, Tooth, ToothStatus} from "../../api-client";
import {getPatientApi} from "../../api/apiInstances";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';

import {AlertContext} from "../../context/AlertContext";
import {isMobileOnly} from "react-device-detect";
import {useForm} from "antd/es/form/Form";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";
import QuickStatusPalette from "./QuickStatusPalette";
import {conditionForLetter, isTypingTarget, normalizeShortcutKey} from "../../utils/conditionShortcuts";

interface PatientTreatmentConditionModalProps {
    mode: 'create' | 'edit';
    loadData: Function;
    appointment: string;
    statuses: StatusResponse[];
    selectedStatuses: StatusResponse[];
    selectedTableStatus: ToothStatus;
    allTeethStatuses: ToothStatus[];
    allTeethPositions: Tooth[];
    toothObjs: Tooth[];
    title: string | undefined;
    isModalOpen: boolean;
    setIsModalOpen: Function;
}

interface DisplayCondition {
    uid: string;
    id: string;
    nzisId: string;
}

interface TagifyItem {
    value: string;
}

const PatientTreatmentStatusModal = ({
                                         mode,
                                         appointment,
                                         loadData,
                                         statuses,
                                         selectedTableStatus,
                                         selectedStatuses,
                                         allTeethStatuses,
                                         allTeethPositions,
                                         toothObjs,
                                         title,
                                         isModalOpen,
                                         setIsModalOpen
                                     }: PatientTreatmentConditionModalProps) => {

    const intl = useIntl();

    const {locale} = useContext(DentobookApplicationContext);
    const {setVisible, setError} = useContext(AlertContext);

    const [loading, setLoading] = useState<boolean>(false);

    const [fieldsError, setFieldsError] = useState<string>('');

    const [positions, setPositions] = useState<number[]>([]);
    const [conditions, setConditions] = useState<DisplayCondition[]>([]);

    const [conditionOptions, setConditionOptions] = useState<Array<any>>([]);

    const [notes, setNotes] = useState<string>();

    const patientApi = getPatientApi();

    const tagifyRef: Ref<any> = useRef();

    const richTextEditorToolbarOptions = [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        ['bold', 'italic', 'underline', 'strike'],
        [{'align': []}],
    ];

    const [form] = useForm();

    const toothObj = toothObjs.length && toothObjs[0] ? toothObjs[0] : undefined;

    useEffect(() => {
        setPositions(toothObjs.filter(toothObj => toothObj).map(toothObj => toothObj?.position!));

        const quillElement = document.getElementsByClassName('ql-editor')[0];

        if (!isModalOpen || mode === 'create') {
            setConditions([{
                uid: uuidv4(),
                id: '',
                nzisId: ''
            }]);

            if (notes && quillElement) {
                quillElement.innerHTML = '';
            }
            setNotes('');
        } else {
            const temp = allTeethStatuses.filter(statusObj => statusObj.tooth?.id === toothObj?.id);
            if (temp.length && quillElement) {
                quillElement.innerHTML = temp[0].notes || '';
                setNotes(temp[0].notes ?? undefined);
            }
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (!selectedStatuses?.length) {
            // In edit mode the status may be archived (not in the active `statuses` list).
            // Initialise from selectedTableStatus so the archived UUID is still shown
            // and the user sees the archived warning instead of a blank condition field.
            if (mode === 'edit' && selectedTableStatus?.statusId) {
                const uid = uuidv4();
                const id = selectedTableStatus.statusId;
                const nzisId = selectedTableStatus.nzisStatusId || '';
                setConditions([{uid, id, nzisId}]);
                form.setFieldValue('condition-' + uid, id);
            } else {
                setConditions([{
                    uid: uuidv4(),
                    id: '',
                    nzisId: ''
                }]);
            }

            return;
        }

        if (selectedStatuses.length) {
            if (!toothObj) {
                return;
            }

            const toothStatus = allTeethStatuses.find(statusObject => statusObject.tooth?.id === toothObj.id);
            setNotes((toothStatus?.notes) || '');

            const conditionsArray: DisplayCondition[] = [];

            for (const selectedStatus of selectedStatuses) {
                const uid = uuidv4();
                const id = selectedStatus.id!;
                let nzisId = '';
                if (mode === 'edit') {
                    nzisId = selectedTableStatus.nzisStatusId!;
                }

                conditionsArray.push({uid, id, nzisId});

                form.setFieldValue('condition-' + uid, id);
            }

            setConditions(conditionsArray);

            return;
        }

        if (statuses.length === 0) {
            return;
        }

        setConditions([{
            uid: uuidv4(),
            id: '',
            nzisId: ''
        }]);
    }, [selectedStatuses]);

    const handleCancel = () => {
        setFieldsError('');
        setIsModalOpen(false);
    }

    const handleOk = () => {
        form.validateFields()
            .then(() => {
                let invalidToothFlag = false;
                const teethToPost = tagifyRef.current.value.map((element: TagifyItem) => {
                    const whitelist = allTeethPositions.map(toothPosition => toothPosition?.position!.toString());

                    return allTeethPositions.filter(toothObj => {
                        const toothPosition = +toothObj?.position!;
                        const tagifyValue = +element.value;

                        if (whitelist.indexOf(element.value) === -1) {
                            invalidToothFlag = true;
                        }

                        return toothPosition === tagifyValue;
                    })[0];
                });

                if (invalidToothFlag) {
                    setFieldsError(intl.formatMessage({id: 'invalid_tooth_position'}));

                    return;
                }

                if (conditions.length === 0) {
                    return;
                }

                if (!appointment) {
                    logger.log("appointment id is undefined.....");
                    return;
                }

                const dataToPost = [];

                for (const toothToPost of teethToPost) {
                    for (const conditionToPost of conditions) {
                        dataToPost.push({
                            toothId: toothToPost.id,
                            statusId: conditionToPost.id,
                            nzisStatusId: conditionToPost.nzisId || null,
                            notes,
                        });
                    }
                }

                if (teethToPost.length === 0) {
                    for (const conditionToPost of conditions) {
                        dataToPost.push({
                            toothId: null,
                            statusId: conditionToPost.id,
                            nzisStatusId: conditionToPost.nzisId || null,
                            notes
                        });
                    }
                }

                setLoading(true);
                if (mode === 'create') {
                    patientApi.createToothStatus(appointment, dataToPost)
                        .then(() => {
                            setLoading(false);
                            setVisible(true);
                            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                            loadData();

                            setIsModalOpen(false);
                        })
                        .catch(err => logger.log(err))
                } else {
                    logger.log({appointment, a: selectedTableStatus.id!, b: dataToPost[0]});
                    patientApi.updateToothStatus(appointment, selectedTableStatus.id!, dataToPost[0])
                        .then(() => {
                            setLoading(false);
                            setVisible(true);
                            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                            loadData();

                            setIsModalOpen(false);
                        })
                        .catch(err => logger.log(err))
                }
            })
            .catch(err => {
                logger.log(err);

                const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;

                if (blankFieldExists) {
                    setError(intl.formatMessage({id: 'all_fields_required'}));
                    setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                }
            });
    }

    const handleFilter = (input: string, option: any) => option.label.toLowerCase().includes(input.toLowerCase());

    useEffect(() => {
        setConditionOptions(statuses.sort((statusOne, statuseTwo) => (statusOne.displayName ?? '').localeCompare(statuseTwo.displayName ?? ''))
            .map(status => {
                return {value: status.id, label: status.displayName ?? status.name ?? status.id};
            }));
    }, [statuses]);

    // Quick-status shortcut: select the condition in the FIRST condition row
    // (both the controlled `conditions` state and the AntD form store, so the
    // Select shows it and validation passes). Never auto-saves — the user still
    // confirms, keeping notes-entry intact.
    const applyShortcut = (status: StatusResponse) => {
        setConditions(prev => {
            if (prev.length === 0) {
                const uid = uuidv4();
                form.setFieldValue('condition-' + uid, status.id);
                return [{uid, id: status.id, nzisId: ''}];
            }
            form.setFieldValue('condition-' + prev[0].uid, status.id);
            return prev.map((cond, idx) => idx === 0 ? {...cond, id: status.id} : cond);
        });
    };

    // Keyboard shortcuts inside the modal only SELECT (never save), and only when
    // focus is outside any text-entry surface (notes / search / position editor).
    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (isTypingTarget(document.activeElement)) {
                return;
            }
            const letter = normalizeShortcutKey(event.key);
            if (!letter) {
                return;
            }
            const conditionDefinition = conditionForLetter(letter, statuses);
            if (!conditionDefinition) {
                return;
            }
            event.preventDefault();
            applyShortcut(conditionDefinition);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, statuses]);

    const modalCreateTitle = useIntl().formatMessage({id: 'add_condition_title'});

    const footer = <div className="modal-btn-wrapper">
        <Button className="modal-btn" onClick={handleCancel}>
            {<FormattedMessage id="cancel"/>}
        </Button>
        <Button className="modal-btn" type="primary" onClick={handleOk}
        >
            {<FormattedMessage id="confirm"/>}
        </Button>
    </div>

    return (
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            footer={footer}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={title ?? modalCreateTitle}
            width={800}
            styles={{body: {overflowY: 'auto', maxHeight: 'calc(100vh - 300px)'}}}
            confirmLoading={loading}
            onCancel={handleCancel}
            closeIcon={<CloseOutlined className={'icon-24px'}/>}
            open={isModalOpen}>
            <Form layout="vertical" form={form}>
                {fieldsError && <p className="text-red">{fieldsError}</p>}
                <Form.Item label={<FormattedMessage id="position"/>}>
                    <div className="borderless teeth-tagify">
                        <Tags
                            onChange={() => setPositions(tagifyRef.current.value)}
                            value={positions} tagifyRef={tagifyRef}
                            whitelist={allTeethPositions.map(toothPosition => toothPosition.position?.toString())}
                            enforceWhitelist={false}
                            disabled={mode === 'edit'}
                            settings={{
                                enforceWhitelist: false,
                                blacklist: [],
                                maxTags: 100,
                                dropdown: {
                                    enabled: 0
                                },
                                callbacks: {}
                            }}/>
                    </div>
                </Form.Item>
                {statuses.length > 0 && (
                    <Form.Item label={<FormattedMessage id="quick_status"/>}
                               className="dentobook-form-item">
                        <QuickStatusPalette
                            conditionDefinitions={statuses}
                            activeConditionId={conditions[0]?.id || null}
                            onSelect={applyShortcut}
                            className="quick-status-palette-inline"
                        />
                    </Form.Item>
                )}
                {conditions.map((condition) => {
                    const conditionArchived = mode === 'edit'
                        && !!condition.id
                        && !conditionOptions.some(o => o.value === condition.id);
                    // Prefer live entity label; fall back to snapshot on the ToothStatus
                    // record (selectedTableStatus); last resort: the raw id string.
                    const archivedLabel = conditionArchived
                        ? (selectedStatuses?.find(s => s.id === condition.id)?.displayName
                            ?? selectedTableStatus?.statusNameSnapshot
                            ?? condition.id)
                        : undefined;
                    // Inject a disabled synthetic option so the Select renders the archived
                    // label instead of a raw UUID — same pattern as the treatment modal.
                    const conditionOptionsForSelect = conditionArchived && condition.id && archivedLabel
                        ? [{value: condition.id, label: archivedLabel, disabled: true}, ...conditionOptions]
                        : conditionOptions;
                    return (
                        <React.Fragment key={condition.uid}>
                            <Row gutter={12}>
                                <Col md={24}>
                                    <Form.Item name={"condition-" + condition.uid}
                                               className={"dentobook-form-item input-full-width"}
                                               label={<FormattedMessage id="condition"/>}
                                               rules={[{required: !conditionArchived, message: <FormattedMessage id="required_field"/>}]}>
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder={<FormattedMessage id="selectPlaceholder"/>}
                                            size={"large"}
                                            className="dentobook-input"
                                            style={isMobileOnly ? {width: '88vw'} : {}}
                                            suffixIcon={<CaretDownOutlined/>}
                                            filterOption={handleFilter}
                                            options={conditionOptionsForSelect}
                                            value={condition.id}
                                            onChange={e => {
                                                setConditions(conditions.map(cond => {
                                                    if (cond.uid === condition.uid) {
                                                        cond.id = e;
                                                    }
                                                    return cond;
                                                }));
                                            }}
                                            allowClear={true}
                                        />
                                    </Form.Item>
                                    {conditionArchived && (
                                        <Alert
                                            type="warning"
                                            style={{marginTop: -16, marginBottom: 8}}
                                            message={<><FormattedMessage id="archived_condition"/>: {archivedLabel}</>}
                                        />
                                    )}
                                </Col>
                            </Row>
                            {conditions.length > 1 ? <Button className="text-black"
                                                             icon={<DeleteOutlined/>}
                                                             type="link"
                                                             onClick={() => setConditions(conditions.filter(
                                                                 (cond) => cond.uid !== condition.uid)
                                                             )}></Button> : null}
                        </React.Fragment>
                    );
                })}
                <Form.Item
                    label={<FormattedMessage id="notes"/>}
                    name="notes"
                    initialValue={notes}
                    className="dentobook-form-item dentobook-simple-input mt-12"
                >
                    <ReactQuill
                        id="notesQuill"
                        value={notes}
                        onChange={e => setNotes(e)}
                        modules={{
                            toolbar: richTextEditorToolbarOptions
                        }}/>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PatientTreatmentStatusModal;
